import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Modal
} from "shared/components";
import {
  getVendorAccountErrorCode,
  getVendorAccountErrorMessage,
  lockboxAccountsApi,
  VendorAccount
} from "api/lockbox-accounts";
import type { LockboxVendor } from "api/landlord/lockboxes/types";
import { useAuthContext } from "providers/Auth";
import { VENDOR_LABELS } from "views/lockboxes/constants";

interface AddLockboxAccountModalProps {
  vendor: LockboxVendor;
  open: boolean;
  existingAccounts: VendorAccount[];
  onClose: () => void;
  onSuccess: (account: VendorAccount) => void;
}

export const AddLockboxAccountModal = ({
  vendor,
  open,
  existingAccounts,
  onClose,
  onSuccess
}: AddLockboxAccountModalProps) => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [nameError, setNameError] = useState("");
  const [usernameError, setUsernameError] = useState("");

  const reset = () => {
    setName("");
    setUsername("");
    setPassword("");
    setNameError("");
    setUsernameError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: createAccount, isPending } = useMutation({
    mutationKey: ["LOCKBOX_ACCOUNTS", vendor, "CREATE"],
    mutationFn: () =>
      lockboxAccountsApi.create(vendor, {
        name: name.trim(),
        username: username.trim(),
        password
      }),
    onSuccess: (account) => {
      toast.success(`${VENDOR_LABELS[vendor]} credentials added`);
      queryClient.invalidateQueries({
        queryKey: ["LOCKBOX_ACCOUNTS", vendor]
      });
      refreshPartnerData();
      reset();
      onSuccess(account);
    },
    onError: (err) => {
      const message = getVendorAccountErrorMessage(err);
      const code = getVendorAccountErrorCode(err);
      if (code === "duplicate_name") setNameError(message);
      else if (code === "duplicate_username") setUsernameError(message);
      else toast.error(message);
    }
  });

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (existingAccounts.some((account) => account.name === trimmedName)) {
      setNameError("An account with this name already exists for this vendor");
      return;
    }

    const trimmedUsername = username.trim();
    if (
      existingAccounts.some((account) => account.username === trimmedUsername)
    ) {
      setUsernameError(
        "Another account for this vendor already uses this login"
      );
      return;
    }

    createAccount();
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      panelClassName="w-full max-w-[440px] p-6 space-y-6"
    >
      <h2 className="text-xl font-bold text-content-1000">
        Add {VENDOR_LABELS[vendor]} credentials
      </h2>
      <div className="space-y-4">
        <BaseInput
          label="Account name"
          required
          id={`${vendor}-account-name`}
          placeholder="e.g. Downtown Portfolio"
          maxLength={255}
          value={name}
          error={nameError}
          onChange={(value) => {
            setName(value);
            if (nameError) setNameError("");
          }}
        />
        <BaseInput
          label="Username"
          required
          id={`${vendor}-account-username`}
          placeholder="Username"
          maxLength={255}
          value={username}
          error={usernameError}
          onChange={(value) => {
            setUsername(value);
            if (usernameError) setUsernameError("");
          }}
        />
        <BaseInput
          label="Password"
          required
          id={`${vendor}-account-password`}
          type="password"
          placeholder="**********************"
          maxLength={255}
          value={password}
          onChange={setPassword}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={handleClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
          onClick={handleSubmit}
          loading={isPending}
          disabled={!name.trim() || !username.trim() || !password}
        >
          Add
        </Button>
      </div>
    </Modal>
  );
};
